import {
  AdaptedEntity,
  GenericEntityType,
  KindEnum,
  ReturnFieldEnum,
  TemplateEntity,
  TextFieldValue,
} from '../types';
import { parseEntityRef } from '@mainstage/catalog-model';
import {
  generateURNByKind,
  transformUrnToWitboostId,
} from '@agilelab/plugin-wb-builder-common';
import { compactCompoundEntityRef } from '@agilelab/plugin-wb-platform-common';
import { asEntity } from '../../../types';

export const selectTemplateEntity = (
  availableKinds: TemplateEntity[],
  selectedKind: string,
) => {
  return availableKinds.find(
    availableKind => availableKind.type === selectedKind,
  );
};

/**
 * Removes transient/internal properties (e.g. `__treeId`, `__treePath`,
 * `__searchResult`, `__loadingParentId`) from an entity-like object before
 * it is persisted as a selected value or used to derive a selection
 * identity. These properties are populated differently depending on how the
 * entity was fetched (tree expansion vs. search), so keeping them would make
 * the same concept resolve to a different stored value/identity depending on
 * context, breaking selection matching across searches. `__metadata` is
 * preserved since it carries the entity's canonical name.
 */
export function stripInternalEntityProps<T extends Record<string, unknown>>(
  obj: T,
): T {
  const result = {} as T;
  for (const key of Object.keys(obj)) {
    if (key.startsWith('__') && key !== '__metadata') continue;
    (result as Record<string, unknown>)[key] = obj[key];
  }
  return result;
}

/**
 * JSON.stringify variant that sorts object keys recursively, so that two
 * objects with the same content but different key insertion order produce
 * the same string. Used to derive stable selection identities from
 * object-valued entities.
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  const entries = keys.map(
    key =>
      `${JSON.stringify(key)}:${stableStringify(
        (value as Record<string, unknown>)[key],
      )}`,
  );
  return `{${entries.join(',')}}`;
}

// TODO: refactor for extensibility (i.e., support multiple providers with different object types)
export function adaptEntity(entity: GenericEntityType): {
  name: string;
  kind: KindEnum;
  label: string;
  urn: string;
  ref: string;
} {
  const castedEntity = asEntity(entity);
  if (castedEntity) {
    return {
      name: entity.metadata.name,
      kind: entity.__metadata?.kind ?? entity.kind,
      label: entity.spec?.mesh?.name ?? entity.metadata?.name,
      urn: generateURNByKind(entity.metadata.name, entity.kind, true),
      ref: compactCompoundEntityRef({
        kind: castedEntity.kind,
        name: castedEntity.metadata.name,
        namespace: 'default',
      }),
    };
  }

  const consumableInterfaceRef = transformUrnToWitboostId(entity.id);
  if (!consumableInterfaceRef) {
    throw new Error(
      `Invalid URN. Check your entity id: ${JSON.stringify(entity)}`,
    );
  }
  const consumableInterfaceCompoundRef = parseEntityRef(consumableInterfaceRef);

  return {
    name: consumableInterfaceCompoundRef.name,
    kind: entity.__metadata?.kind ?? KindEnum.consumable,
    label: entity.name,
    urn: entity.id,
    // for consumableInterface only urn or fullObj could be returned
    // for fault tolerance urn will be returned if ref is selected on the template
    ref: entity.id,
  };
}
/**
 * Transforms the raw entity into a TextFieldValue which can be understood by the UI, with final value based on the template configuration (i.e., returnField or fieldsToSave).
 * @param entity the raw entity fetched from the remote source, with all its original fields.
 * @param adaptedEntity The entity already adapted to have a common shape with name, kind, label, urn and ref.
 * @param templateEntity The template configuration related to the provider of the selected entity. It is needed to understand how to transform the entity (i.e., which field to return as value, how to compose the label, etc.)
 * @param isAiGenerated A boolean indicating whether the entity was generated by AI.
 * @returns A TextFieldValue representing the transformed entity. Its "value" is what gets stored in the form.
 */
export function getLabelAndValue(
  entity: GenericEntityType,
  adaptedEntity: AdaptedEntity,
  templateEntity?: TemplateEntity,
  isAiGenerated = false,
): TextFieldValue {
  let result: TextFieldValue;

  if (!templateEntity) {
    result = {
      kind: KindEnum.arbitrary,
      label: entity.__metadata.name,
      value: stripInternalEntityProps(entity),
      isAiGenerated,
    };
  } else {
    const kindDisplayName = templateEntity.displayField?.includes('ref')
      ? undefined
      : templateEntity.displayName ?? templateEntity.type;

    if ('returnField' in templateEntity) {
      if (templateEntity.returnField?.toLowerCase() === ReturnFieldEnum.urn) {
        result = {
          kind: adaptedEntity.kind,
          kindDisplayName,
          label: adaptedEntity.label,
          value: adaptedEntity.urn,
          isAiGenerated,
        };
      } else if (
        templateEntity.returnField?.toLowerCase() === ReturnFieldEnum.ref
      ) {
        result = {
          kind: adaptedEntity.kind,
          kindDisplayName,
          label: adaptedEntity.label,
          value: adaptedEntity.ref,
          isAiGenerated,
        };
      } else if (
        templateEntity.returnField?.toLowerCase() === ReturnFieldEnum.full
      ) {
        result = {
          kind: adaptedEntity.kind,
          kindDisplayName,
          label: adaptedEntity.label,
          value: stripInternalEntityProps(entity),
          isAiGenerated,
        };
      } else if (
        templateEntity.returnField?.toLowerCase() === ReturnFieldEnum.iri
      ) {
        result = {
          kind: adaptedEntity.kind,
          kindDisplayName,
          label: adaptedEntity.label,
          value: adaptedEntity.ref,
          isAiGenerated,
        };
      } else {
        result = {
          kind: adaptedEntity.kind,
          label: adaptedEntity.label,
          value: adaptedEntity.urn,
          isAiGenerated,
        };
      }
    } else if (
      'fieldsToSave' in templateEntity &&
      Array.isArray(templateEntity.fieldsToSave)
    ) {
      const filteredObj = Object.fromEntries(
        templateEntity.fieldsToSave
          .filter(key => key in entity)
          .map(key => [key, entity[key]]),
      );
      result = {
        kind: adaptedEntity.kind,
        kindDisplayName,
        label: adaptedEntity.label,
        // TODO do we need to remove metadata?
        value: stripInternalEntityProps({
          ...filteredObj,
          __metadata: entity.__metadata,
        }),
        isAiGenerated,
      };
    } else {
      result = {
        kind: adaptedEntity.kind,
        label: adaptedEntity.label,
        value: adaptedEntity.urn,
        isAiGenerated,
      };
    }
  }

  if (adaptedEntity.treePath) {
    result.treePath = adaptedEntity.treePath;
  }

  return result;
}
